import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/redux/reducers/rootReducer';
import { useTranslation } from 'react-i18next';
import {
  chakraCustomScrollBar,
  mq,
} from '../../../../common/src/utils/styledComponentsUtils';
import Avatar from '../default/ProdFacAvatar';
import SelectedOffersSummary from './selected-offer-summary/SelectedOffersSummary';
import OfferAcknowledgements from './OfferAcknowledgements';
import SelectedOffersContainerOverview from '../default/offers/SelectedOffersContainerOverview';
import { Box, Text, Grid } from '@wegroup/design-system';
import SelectedOffersFooter from './SelectedOffersFooter';
import OfferInfoInputs from './OfferInfoInputs';
import LoadingSpinner from '../../../../common/src/components/LoadingSpinner';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';

interface Props {
  isProductsLoading?: boolean;
}

const SelectedOffersContainer: React.FC<React.PropsWithChildren<Props>> = ({
  isProductsLoading,
}) => {
  const { t } = useTranslation();

  const selectedOffers = useSelector(
    (state: RootState) => state.offers.selectedOffers,
  );

  const selectedRiskObjects = useSelector(
    (state: RootState) => state.selection.selectedRiskObjects,
  );

  const { campaign } = useAuth();
  const showAcknowledgements = campaign?.show_acknowledgements;

  const chosenRiskObjects = Object.keys(selectedOffers);

  if (isProductsLoading) return <LoadingSpinner />;

  return (
    <Box
      overflow="auto"
      sx={chakraCustomScrollBar}
      bg={{ base: 'white', laptop: 'unset' }}
      pb="6"
      id="SelectedOffersMainContainer"
    >
      {Object.values(selectedOffers).some((array) => array.length > 0) && (
        <Box
          padding={4}
          background="#FFF8F1"
          width="100%"
          display={{ laptop: 'none' }}
        >
          <Box background="white" borderRadius="6px" width="100%">
            <SelectedOffersSummary />
          </Box>
        </Box>
      )}
      <SelectedOffersContainerOverview
        selectedRiskObjects={selectedRiskObjects}
        selectedOffers={selectedOffers}
      />
      {chosenRiskObjects.length > 0 && (
        <Grid
          width="100%"
          mx="auto"
          gridGap={{ base: 0, laptop: '6' }}
          justifyContent="center"
          gridTemplateColumns={{
            base: '1fr',
            laptop: '7.6rem minmax(auto, 100rem)',
          }}
          padding={{
            base: '4',
            laptop: '6',
          }}
          maxW="104.8rem"
        >
          <StyledAvatar />
          <Box gridColumn="2">
            <Box mb="4" maxW="68rem" id="AcknowledgementContainer">
              <Text
                textStyle="h1Headline"
                as="h1"
                mt="3"
                mb="4"
                display={{ base: 'none', laptop: 'block' }}
              >
                {t(
                  'Before requesting your offers, there’s a few more things we need to know',
                )}
              </Text>
            </Box>
            {showAcknowledgements !== false && (
              <OfferAcknowledgements riskObjects={chosenRiskObjects} />
            )}
            <OfferInfoInputs />
            <SelectedOffersFooter />
          </Box>
        </Grid>
      )}
    </Box>
  );
};

const StyledAvatar = styled(Avatar)`
  display: none;

  ${mq.laptop} {
    display: block;
    width: 7.6rem;
    height: 7.6rem;
  }
`;

export default SelectedOffersContainer;
